/**
 * <user-profile-view> — wraps the server-rendered profile.html view and
 * keeps it live across the same user's other tabs / browsers / devices.
 *
 * The template is SSR Jinja and not re-rendered on update — this element
 * just patches the existing DOM spans when a `user_profile_updated` event
 * arrives on `luboo:user:{userId}` (data layer fans it out from the
 * server's broadcast_event(channel="user:{id}", ...)).
 *
 * Used by user / account / property shells (registered via
 * register-shared-elements.js) because profile.html is shared.
 */

import { setupChannel, destroyChannel } from '../mixins/data-channel.mixin.js';

class UserProfileView extends HTMLElement {
  setupChannel = setupChannel;
  destroyChannel = destroyChannel;

  _messageMap = {
    user_profile_updated: this._onUserProfileUpdated,
  };

  connectedCallback() {
    const userId = window.luboo?.data?._userId;
    if (!userId) return;
    this.setupChannel(`luboo:user:${userId}`);
  }

  disconnectedCallback() {
    this.destroyChannel();
  }

  _onUserProfileUpdated(user) {
    if (!user) return;
    const set = (id, value) => {
      const el = this.querySelector(`#${id}`);
      if (el && value != null) el.textContent = value;
    };
    set('profile-username', user.username);
    set('profile-email', user.email);
    set('profile-phone', user.phone || 'Not set');
    set('profile-timezone', user.timezone);
    set('profile-locale', user.locale);
    // Full name is rendered only when first or last name is set; mirror
    // that here so the line appears on update if it wasn't there before.
    const full = [user.first_name, user.last_name].filter(Boolean).join(' ');
    let fullEl = this.querySelector('#profile-fullname');
    if (full) {
      if (!fullEl) {
        const phoneRow = this.querySelector('#profile-phone')?.closest('p');
        const row = document.createElement('p');
        row.innerHTML = '<strong>Full Name:</strong> <span id="profile-fullname"></span>';
        phoneRow?.parentNode?.insertBefore(row, phoneRow);
        fullEl = row.querySelector('#profile-fullname');
      }
      fullEl.textContent = full;
    } else if (fullEl) {
      fullEl.closest('p')?.remove();
    }
  }
}

export { UserProfileView };
